import { PDFDocument, degrees, rgb, LineCapStyle } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { TEXT_ASCENT, TEXT_LINE_HEIGHT, localToNative, nativeToPdf, totalRotation } from "./geometry";

export const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  const n = m ? parseInt(m[1], 16) : 0;
  return rgb(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
};

const dataUrlToBytes = (dataUrl) => {
  const base64 = String(dataUrl).split(",").pop();
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
};

const isJpeg = (dataUrl) => /^data:image\/jpe?g/i.test(dataUrl);

/**
 * สร้าง PDF ใหม่จากหน้าที่แก้แล้ว
 * @param sources { [docId]: Uint8Array } ไฟล์ต้นทาง (ต้นฉบับ + ไฟล์ที่รวมเพิ่ม)
 * @param pages [{ docId, srcIndex, baseRotation, userRotation, objects }]
 * @param fonts { regular: Uint8Array, bold: Uint8Array } ฟอนต์ไทย (Sarabun)
 */
export async function exportPdf({ sources, pages, fonts }) {
  const out = await PDFDocument.create();
  out.registerFontkit(fontkit);

  const loaded = {};
  for (const docId of Object.keys(sources)) {
    loaded[docId] = await PDFDocument.load(sources[docId], { ignoreEncryption: true });
  }

  const fontCache = {};
  const getFont = async (bold) => {
    const key = bold ? "bold" : "regular";
    if (!fontCache[key]) fontCache[key] = await out.embedFont(fonts[key], { subset: true });
    return fontCache[key];
  };
  const imageCache = new Map();
  const getImage = async (src) => {
    if (!imageCache.has(src)) {
      const bytes = dataUrlToBytes(src);
      imageCache.set(src, await (isJpeg(src) ? out.embedJpg(bytes) : out.embedPng(bytes)));
    }
    return imageCache.get(src);
  };

  for (const page of pages) {
    const [copied] = await out.copyPages(loaded[page.docId], [page.srcIndex]);
    const pdfPage = out.addPage(copied);
    const R = totalRotation(page);
    pdfPage.setRotation(degrees(R));
    const box = pdfPage.getCropBox();
    const toPdf = (obj, lx, ly) => {
      const n = localToNative(obj, lx, ly);
      return nativeToPdf(n.x, n.y, box);
    };

    for (const obj of page.objects || []) {
      const color = hexToRgb(obj.color);

      if (obj.type === "stroke" || obj.type === "highlight") {
        const pts = obj.points || [];
        if (!pts.length) continue;
        const d = pts.length === 1
          ? `M ${pts[0].x} ${pts[0].y} L ${pts[0].x + 0.01} ${pts[0].y}`
          : pts.map((p, i) => `${i ? "L" : "M"} ${p.x} ${p.y}`).join(" ");
        // drawSvgPath: path อยู่ในแกน y ชี้ลง ต้นทางที่ (x,y) = มุมบนซ้ายของ native
        pdfPage.drawSvgPath(d, {
          x: box.x,
          y: box.y + box.height,
          borderColor: color,
          borderWidth: obj.width,
          borderOpacity: obj.type === "highlight" ? 0.35 : 1,
          borderLineCap: LineCapStyle.Round,
        });
        continue;
      }

      const angle = degrees(-(obj.rot || 0)); // pdf-lib หมุนทวนเข็ม, rot ของเราตามเข็ม

      if (obj.type === "image") {
        const img = await getImage(obj.src);
        const bl = toPdf(obj, 0, obj.h);
        pdfPage.drawImage(img, { x: bl.x, y: bl.y, width: obj.w, height: obj.h, rotate: angle, opacity: obj.opacity ?? 1 });
      } else if (obj.type === "rect") {
        const bl = toPdf(obj, 0, obj.h);
        pdfPage.drawRectangle({
          x: bl.x, y: bl.y, width: obj.w, height: obj.h, rotate: angle,
          borderColor: color, borderWidth: obj.width,
        });
      } else if (obj.type === "text") {
        const font = await getFont(obj.bold);
        const lines = String(obj.text || "").split("\n");
        for (let i = 0; i < lines.length; i += 1) {
          if (!lines[i]) continue;
          const p = toPdf(obj, 0, obj.size * (TEXT_ASCENT + i * TEXT_LINE_HEIGHT));
          pdfPage.drawText(lines[i], { x: p.x, y: p.y, size: obj.size, font, color, rotate: angle });
        }
      }
    }
  }

  return out.save();
}
